/* eslint-disable @typescript-eslint/no-explicit-any */
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  AfterContentInit,
  Component,
  ContentChildren,
  DestroyRef,
  effect,
  HostListener,
  inject,
  input,
  Input,
  QueryList,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { Dropdown, DropdownModule } from 'primeng/dropdown';
import { InputText, InputTextModule } from 'primeng/inputtext';
import { Nullable } from 'primeng/ts-helpers';
import { debounceTime, Subscription } from 'rxjs';

import { AvatarComponent } from '../../components/avatar/avatar.component';
import { TemplateDirective } from '../../directives/template.directive';
import { CallPipe } from '../../pipes/call.pipe';
import { cast } from '../../utils';
import { SmartPickerHandler } from '../smart-input-entity/smart-picker.handler';
import { BaseSmartInputComponent } from '../smart-input-string/base-smart-input.component';

//#region PrimeNg '@primeuix/utils';

const isDate = (value: unknown) => {
  return Object.prototype.toString.call(value) === '[object Date]';
};

const isEmpty = (value: unknown) => {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (!isDate(value) && typeof value === 'object' && Object.keys(value).length === 0)
  );
};

const isNotEmpty = (value: unknown) => {
  return !isEmpty(value);
};

const isPrintableCharacter = (char = '') => {
  return isNotEmpty(char) && char.length === 1 && char.match(/\S| /);
};

//#region

@Component({
  selector: 'app-smart-input-entity2',
  templateUrl: './smart-input-entity2.component.html',
  styleUrl: './smart-input-entity2.component.scss',
  standalone: true,
  imports: [
    AsyncPipe,
    AvatarComponent,
    CallPipe,
    DropdownModule,
    FormsModule,
    InputTextModule,
    NgTemplateOutlet,
    ReactiveFormsModule,
    SvgIconComponent,
    TranslateModule,
  ],
})
export class SmartInputEntity2Component
  extends BaseSmartInputComponent<string | undefined>
  implements AfterContentInit
{
  private _dropdown?: Dropdown;
  @ViewChild(Dropdown, { static: false }) set dropdown(dropdown: Dropdown) {
    if (dropdown) {
      this.handler().setDropdown(dropdown);
    }
    this._dropdown = dropdown;
  }
  get dropdown(): Dropdown | undefined {
    return this._dropdown;
  }

  private _searchInput!: InputText;
  @ViewChild(InputText, { static: false }) set searchInput(searchInput: InputText) {
    this._searchInput = searchInput;
  }

  @ContentChildren(TemplateDirective) templates: Nullable<QueryList<TemplateDirective>>;

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    // custom searchbox
    if (this._dropdown?.overlayVisible) {
      // in case of printable character, give the searchbox the focus
      if (isPrintableCharacter(event.key)) {
        this._searchInput.el.nativeElement.focus();
        // append the first char to the searchbox if empty
        if (!this.filterQuery.value?.length) {
          this.filterQuery.setValue(event.key);
          return;
        }
        return;
      } else {
        // propagate other event to the dropdown handler
        this._dropdown?.onKeyDown(event, false);
      }
    }
  }

  private readonly destroyRef = inject(DestroyRef);

  readonly handler = input.required<SmartPickerHandler<any>>();

  @Input() fieldLabel = 'name';

  private _needReset = false;

  itemTemplate?: TemplateRef<{ item: string }>;

  selectedItemTemplate?: TemplateRef<{ item: string }>;

  readonly cast = cast<string>;

  filterQuery = new FormControl('');

  private _itemsSub?: Subscription;

  ngAfterContentInit(): void {
    (this.templates as QueryList<TemplateDirective>).forEach((item) => {
      switch (item.getType()) {
        case 'item':
          this.itemTemplate = item.template;
          break;

        case 'selected-item':
          this.selectedItemTemplate = item.template;
          break;

        default:
          this.itemTemplate = item.template;
          break;
      }
    });
  }

  constructor() {
    super();

    // auto select
    effect(() => {
      const handler = this.handler();
      void this.autoSelect(handler);
    });

    // debounced query
    this.filterQuery.valueChanges.pipe(debounceTime(300)).subscribe((value) => this.filterChange(value ?? ''));

    // FIX: scroller height: when items are very few, the dropdown reduces its height.
    // If the items return to being many, the height doesn't adjust.
    // We add a css min height to enforce the right height (primeng calculate just the height property)
    effect(() => {
      const handler = this.handler();
      this._itemsSub?.unsubscribe();
      this._itemsSub = handler.items$.subscribe((items) => {
        const scrollerEl = this._dropdown?.scroller?.getElementRef()?.nativeElement;
        if (scrollerEl) {
          const options = this.getOptions(items);
          const elements = options.length;
          const yPadding = 16;
          const defaultMinimumHeight = handler.options.templateOptions.scrollHeight + yPadding;
          const effectiveHeight = handler.options.templateOptions.itemSize * elements + yPadding;
          const minHeight = effectiveHeight < defaultMinimumHeight ? effectiveHeight : defaultMinimumHeight;
          scrollerEl.style.minHeight = `${minHeight}px`;
        }
      });
      this.destroyRef.onDestroy(() => {
        this._itemsSub?.unsubscribe();
      });
    });
  }

  // Keep this out of the effect() above — an async arrow passed directly to
  // effect() inside a constructor crashes under Storybook's build (#600).
  private async autoSelect(handler: SmartPickerHandler<any>): Promise<void> {
    if (handler.options.preloadStrategy.autoSelectSingleChoice) {
      const singleChoice = await handler.getSingleChoice();
      if (singleChoice && this.control().value !== singleChoice.id) {
        this.control().patchValue(singleChoice.id);
        return;
      }
    }
    if (handler.options.preloadStrategy.autoSelectFirstItem) {
      const firstChoice = await handler.getFirstChoice();
      if (firstChoice && !this.control().value) {
        this.control().patchValue(firstChoice.id);
        return;
      }
    }
  }

  filterChange(evt: string) {
    this.handler().search(evt);
  }

  onModelChange(item: string | undefined) {
    this.handler().setSelectedId(item);

    this._needReset = true;
  }

  onShow() {
    this.handler().open();
  }

  onHide() {
    if (this._needReset) {
      this.handler().reset();
    }
    this._needReset = false;

    this.filterQuery.setValue('');
  }

  readonly getOptions = <T extends { id: string }>(items: T[] | null) => {
    const tmp = () => {
      const value = this.control().value;

      if (items === null) {
        return [value ? value : {}, {}, {}, {}, {}, {}];
      }
      const a = [
        // always put the selected item on top (selected item is excluded from the query)
        ...((items || []).some((i) => i.id === value) || !value ? [] : [value]),
        ...(items || []).map((o) => o.id),
      ];
      return a;
    };
    const options = tmp();
    return options;
  };

  override onCancel() {
    if (this.control().pristine) {
      return;
    }
    const resetValue = this.resetValue();
    if (resetValue) {
      this.control().patchValue(resetValue);
      this.control().markAsPristine();
    }
    this.handler().reset();
  }
}
